import { useQuery } from "@apollo/client";
import { Point } from "geojson";
import { useMemo } from "react";

import { GaiaExpandableMapMarkerAreaFragment } from "~/modules/plannerV2/components/GaiaExpandableMapMarkerArea/fragments.generated";

import { PlannerV2TravelGroupGetTimelineDocument } from "../queries.generated";
import { categorizeMapPoints } from "./data-transform-utils/mapPointCategorization";
import { buildLocationMetadataByShiftIdMap } from "./data-transform-utils/shiftPickupMetadata";
import { enrichTimelineWithPickupData } from "./data-transform-utils/timelineEnrichment";

export type AreasWithAdditionalVisits = GaiaExpandableMapMarkerAreaFragment & {
    location: Point;
    id: string;
    plannerStatusBucket: string;
    hasStagedChanges: boolean;
    earliestPlannerVisitDaysRemaining: number | null;
};

interface UsePlannerV2TravelGroupGetTimelineParams {
    onDate: string;
    travelGroupId: string;
}

export default function usePlannerV2TravelGroupGetTimeline(params: UsePlannerV2TravelGroupGetTimelineParams) {
    const { data, loading, error } = useQuery(PlannerV2TravelGroupGetTimelineDocument, {
        variables: params,
    });

    /**
     * We need to merge the pick up point data into the timeline data
     * For this we create this map
     */
    const locationMetadataByShiftId = useMemo(
        () => buildLocationMetadataByShiftIdMap(data?.mapElements?.points),
        [data?.mapElements?.points],
    );

    /**
     * we need to prep the timeline data a bit
     * - standardise time property across visit/travel event
     * - hide dropoffs/pickups depending on the travel direction and its place in the timeline
     * - determine wether the travel event is related to a custom pick up location to show pick up controls
     */
    const timeline = useMemo(
        () => enrichTimelineWithPickupData(data?.plannerV2TravelGroupGetTimeline.timeline, locationMetadataByShiftId),
        [data?.plannerV2TravelGroupGetTimeline.timeline, locationMetadataByShiftId],
    );

    /**
     * - split pickup points and fw home locations into separate data sets
     * - components will create GeoJSON from these plain arrays
     */
    const { pickupPoints, fieldworkerHomeMarkers } = useMemo(
        () => categorizeMapPoints(data?.mapElements?.points, locationMetadataByShiftId),
        [data?.mapElements?.points, locationMetadataByShiftId],
    );

    const areasWithAdditionalVisits: AreasWithAdditionalVisits[] = useMemo(() => {
        return (
            data?.plannerV2TravelGroupGetAdditionalVisits.map(area => {
                return {
                    ...area.plannerArea,
                    ...area,
                    id: area.plannerArea.id,
                    earliestPlannerVisitDaysRemaining: area.plannerArea.earliestPlannerVisitDaysRemaining ?? null,
                };
            }) ?? []
        );
    }, [data?.plannerV2TravelGroupGetAdditionalVisits]);

    const areasWithAdditionalVisitsById = useMemo(() => {
        return new Map(areasWithAdditionalVisits.map(area => [area.id, area]));
    }, [areasWithAdditionalVisits]);

    const areasFromTimelineWithNoAdditionalVisits = useMemo(
        () =>
            data?.plannerV2TravelGroupGetTimeline.timeline
                ?.filter(item => item.__typename === "GqlPlannerV2Visit")
                .map(item => ({ ...item.plannerArea, earliestPlannerVisitDaysRemaining: null }))
                .filter(area => [...areasWithAdditionalVisitsById.keys()].includes(area.id) === false) ?? [],
        [data?.plannerV2TravelGroupGetTimeline.timeline, areasWithAdditionalVisitsById],
    );

    const visitTimeSlotId = useMemo(() => {
        return data?.plannerV2TravelGroupGetTimeline.timeline?.filter(item => item.__typename === "GqlPlannerV2Visit")[0]
            ?.timeslotId;
    }, [data?.plannerV2TravelGroupGetTimeline]);

    const areasFromTimelineWithNoAdditionalVisitsById = useMemo(
        () => new Map(areasFromTimelineWithNoAdditionalVisits.map(area => [area.id, area])),
        [areasFromTimelineWithNoAdditionalVisits],
    );

    return {
        loading,
        error,
        data: {
            travelGroupMeta: data?.plannerV2TravelGroupGetTimeline,
            timeline,
            visitTimeSlotId,
            pickupPoints,
            fieldworkerHomeMarkers,
            areasWithAdditionalVisits,
            areasWithAdditionalVisitsById,
            areasFromTimelineWithNoAdditionalVisits,
            areasFromTimelineWithNoAdditionalVisitsById,
            routeTravels: data?.mapElements?.plannerTravels ?? [],
        },
    };
}
